import React, { useState, useEffect, useRef } from "react";
import { siteConfig } from "../../config/siteConfig";
import Icon from "../ui/Icon";
import SealChip from "../ui/SealChip";

// Apertura premium del panel : fade + scale 95->100 con el easing firma.
// Bajo prefers-reduced-motion el bloque global de index.css neutraliza la
// duración (animation-duration: .01ms !important) -> apertura instantánea.
const MODAL_OPEN_STYLE_ID = "demo-modal-open-keyframes";
const MODAL_OPEN_CSS = `
@keyframes demoModalIn {
  from { opacity: 0; transform: scale(0.95); }
  to   { opacity: 1; transform: scale(1); }
}
.demo-modal-panel { animation: demoModalIn 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
`;

const DemoModal = ({ isOpen, onClose, onRegister }) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reinicia la demo cada vez que se abre: el modal queda montado de forma
  // permanente (solo devuelve null al cerrar), así que sin esto el estado
  // interno persistiría y al reabrir se vería la pantalla final o una pregunta
  // a medias en lugar de empezar desde cero.
  useEffect(() => {
    if (isOpen) {
      setCurrentIdx(0);
      setSelectedAnswer(null);
      setIsAnswered(false);
      setIsFinished(false);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Inyecta una sola vez el keyframe de apertura (scale-in) sin tocar index.css
  // ni añadir dependencias. Idempotente: si ya existe, no duplica.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.getElementById(MODAL_OPEN_STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = MODAL_OPEN_STYLE_ID;
    style.textContent = MODAL_OPEN_CSS;
    document.head.appendChild(style);
  }, []);

  // Bloquea el scroll del fondo mientras el modal está abierto (si no, la rueda
  // mueve la página de detrás en vez del panel y no se llega al botón "Siguiente").
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const modalRef = useRef(null);
  const answeredRef = useRef(null);
  const triggerRef = useRef(null);

  // Mantiene la última referencia de onClose sin meterla en las dependencias del
  // efecto de accesibilidad: los padres pasan una flecha inline (identidad nueva
  // en cada render), y si el efecto se reejecutara su limpieza devolvería el foco
  // al disparador a media interacción.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Accesibilidad del diálogo: cierre con Escape, foco trasladado al panel al
  // abrir (devuelto al disparador al cerrar) y "focus trap" simple para que el
  // Tab cicle dentro del modal en lugar de escaparse al contenido del fondo.
  useEffect(() => {
    if (!isOpen) return;
    triggerRef.current = document.activeElement;
    const panel = modalRef.current;
    if (panel) panel.focus();

    const getFocusable = () =>
      panel
        ? Array.from(
            panel.querySelectorAll(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )
          ).filter((el) => !el.disabled && el.offsetParent !== null)
        : [];

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const focusable = getFocusable();
      if (focusable.length === 0) {
        e.preventDefault();
        if (panel) panel.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (
        triggerRef.current &&
        typeof triggerRef.current.focus === "function"
      ) {
        triggerRef.current.focus();
      }
    };
  }, [isOpen]);

  // Auto-scroll fluido: al responder, baja suavemente hacia la explicación y el
  // botón "Siguiente" (clic sin buscar); al cambiar de pregunta, vuelve arriba.
  // El usuario puede subir libremente para revisar las respuestas.
  useEffect(() => {
    if (!isOpen) return;
    if (isAnswered) {
      const t = setTimeout(() => {
        if (answeredRef.current) {
          answeredRef.current.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      }, 80);
      return () => clearTimeout(t);
    } else if (modalRef.current) {
      modalRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [isAnswered, currentIdx, isOpen]);

  const DEMO_QUESTIONS = siteConfig.demo.questions;

  // Etiquetas accesibles de corrección leídas de config con respaldo seguro:
  // garantizan que el acierto/error no se transmita solo por color (lectores de
  // pantalla y personas daltónicas) y permiten reescribir el copy sin tocar el JSX.
  const answerCorrectLabel =
    siteConfig.demo.answerCorrectLabel || "Rätt svar";
  const answerIncorrectLabel =
    siteConfig.demo.answerIncorrectLabel || "Fel svar";

  if (!isOpen) return null;

  const handleAnswer = (idx) => {
    if (isAnswered) return;
    setSelectedAnswer(idx);
    setIsAnswered(true);
  };

  const handleNext = () => {
    if (currentIdx >= DEMO_QUESTIONS.length - 1) {
      setIsFinished(true);
    } else {
      setCurrentIdx((prev) => prev + 1);
      setSelectedAnswer(null);
      setIsAnswered(false);
    }
  };

  // Protege contra el doble clic: onRegister cierra esta demo y abre el modal de
  // registro, así que solo debe dispararse una vez aunque se pulse rápido.
  const handleRegister = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    onRegister();
  };

  const question = DEMO_QUESTIONS[currentIdx];
  const progressPercent = ((currentIdx + 1) / DEMO_QUESTIONS.length) * 100;
  const isRight = isAnswered && selectedAnswer === question.c;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-6">
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialogo: altura limitada en TODOS los tamaños (max-h-[88vh]) con scroll interno.
          Apertura premium: .elev-3 + scale-in 95->100 (reduced-motion -> instantáneo). */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="demo-modal-title"
        tabIndex={-1}
        className="demo-modal-panel relative bg-white w-full max-w-2xl max-h-[88vh] rounded-2xl elev-3 overflow-y-auto overscroll-contain focus:outline-none"
      >
        {/* Stängknapp */}
        <button
          onClick={onClose}
          aria-label="Stäng"
          className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {!isFinished && (
          <div className="px-6 pt-7 pr-16 md:px-10 md:pt-9 md:pr-20 flex items-center justify-between gap-4">
            <SealChip label="Provsimulering i skarpt format" icon="seal" />
            <div className="flex items-center gap-3">
              <span className="text-xs md:text-sm font-bold text-slate-400 whitespace-nowrap tabular-nums">
                Fråga {currentIdx + 1}/{DEMO_QUESTIONS.length}
              </span>
              <div className="h-2 w-16 md:w-24 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-700 transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          </div>
        )}

        <div className="p-6 md:p-10 md:pt-8">
          {!isFinished ? (
            <div className="animate-fade">
              <h3
                id="demo-modal-title"
                className="text-lg md:text-2xl font-black text-slate-800 leading-snug mb-6 md:mb-8"
              >
                {question.q}
              </h3>

              <div className="grid gap-3">
                {question.o.map((option, idx) => {
                  const isCorrect = idx === question.c;
                  const isSelected = idx === selectedAnswer;
                  const letter = String.fromCharCode(65 + idx);

                  let buttonClass =
                    "border-slate-100 hover:border-indigo-300 hover:bg-indigo-50/50";
                  let iconClass =
                    "bg-slate-100 text-slate-600 border-2 border-slate-200";
                  let badge = letter;
                  let badgeIcon = null;

                  if (isAnswered) {
                    if (isCorrect) {
                      buttonClass =
                        "border-emerald-500 bg-gradient-to-r from-emerald-50 to-green-50";
                      iconClass =
                        "bg-emerald-500 text-white border-2 border-emerald-600";
                      badge = null;
                      badgeIcon = "check";
                    } else if (isSelected && !isCorrect) {
                      buttonClass =
                        "border-red-500 bg-gradient-to-r from-red-50 to-rose-50";
                      iconClass =
                        "bg-red-500 text-white border-2 border-red-600";
                      badge = null;
                      badgeIcon = "x";
                    } else {
                      buttonClass =
                        "border-slate-100 bg-slate-50/50 opacity-60";
                    }
                  }

                  let statusLabel = "";
                  if (isAnswered) {
                    if (isCorrect) statusLabel = `${answerCorrectLabel}: `;
                    else if (isSelected)
                      statusLabel = `${answerIncorrectLabel}: `;
                  }

                  return (
                    <button
                      key={idx}
                      onClick={() => handleAnswer(idx)}
                      disabled={isAnswered}
                      className={`w-full text-left p-4 md:p-5 rounded-xl border-2 font-bold text-slate-700 transition-all flex items-center gap-3 md:gap-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2 ${buttonClass}`}
                    >
                      <span
                        aria-hidden="true"
                        className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-xl text-xs md:text-sm font-black shrink-0 ${iconClass}`}
                      >
                        {badgeIcon === "check" ? (
                          <Icon
                            name="check"
                            className="w-4 h-4 md:w-5 md:h-5"
                          />
                        ) : badgeIcon === "x" ? (
                          <svg
                            className="w-4 h-4 md:w-5 md:h-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        ) : (
                          badge
                        )}
                      </span>
                      <span className="flex-grow text-sm md:text-base">
                        {statusLabel && (
                          <span className="sr-only">{statusLabel}</span>
                        )}
                        {option}
                      </span>
                    </button>
                  );
                })}
              </div>

              {isAnswered && (
                <div
                  ref={answeredRef}
                  className="mt-6 animate-fade"
                  role="status"
                  aria-live="polite"
                >
                  <div
                    className={`flex items-center gap-2 mb-3 font-black text-sm md:text-base ${
                      isRight ? "text-emerald-600" : "text-red-600"
                    }`}
                  >
                    <Icon
                      name={isRight ? "trophy" : "book"}
                      className="w-5 h-5 shrink-0"
                    />
                    {isRight
                      ? "Rätt!"
                      : "Fel svar — det rätta är markerat i grönt"}
                  </div>
                  <div className="p-4 md:p-5 rounded-xl bg-indigo-50/60 border border-indigo-100 border-l-4 border-l-indigo-600 mb-6">
                    <p className="font-black text-slate-800 mb-1 text-sm md:text-base flex items-center gap-2">
                      <Icon
                        name="bulb"
                        className="w-5 h-5 text-indigo-700 shrink-0"
                      />{" "}
                      Förklaring
                    </p>
                    <p className="text-slate-600 text-xs md:text-sm leading-relaxed">
                      {question.exp}
                    </p>
                  </div>
                  <button
                    onClick={handleNext}
                    className="w-full py-4 bg-slate-900 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2"
                  >
                    {currentIdx === DEMO_QUESTIONS.length - 1
                      ? "Se resultatet"
                      : "Nästa fråga"}
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6 animate-fade pb-10">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-indigo-100 text-indigo-700 rounded-2xl flex items-center justify-center mx-auto mb-6 md:mb-8">
                <Icon name="trophy" className="w-8 h-8 md:w-10 md:h-10" />
              </div>
              <h3
                id="demo-modal-title"
                className="text-2xl md:text-3xl font-black text-slate-900 mb-4"
              >
                Bra jobbat!
              </h3>
              <p className="text-base md:text-lg text-slate-600 mb-8 md:mb-10">
                Du har slutfört den kostnadsfria demon. <br />
                <span className="font-bold text-indigo-700 italic">
                  Vill du fortsätta? Registrera dig gratis i dag.
                </span>
              </p>
              <div className="flex flex-col gap-4">
                <button
                  onClick={handleRegister}
                  disabled={isSubmitting}
                  className="w-full py-4 md:py-5 bg-gradient-to-r from-indigo-700 to-sky-600 text-white text-lg md:text-xl font-black rounded-xl elev-2 glow-cta hover:-translate-y-0.5 active:scale-[.98] transition-all duration-200 ease-out disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2"
                >
                  Registrera dig gratis nyt
                </button>
                <button
                  onClick={onClose}
                  className="text-slate-400 font-bold hover:text-slate-600 underline decoration-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2 rounded"
                >
                  Stäng och gå tillbaka
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoModal;
